import { ConversionError, RateLimitError } from "./WindowsCursorsApi.errors"

import type { CreateWindowsCursorParams, WindowsCursorFile } from "./WindowsCursorsApi.types"

const PATH = "/converters/windows_cursors/"
const FALLBACK_FILENAME = "cursor.cur"
const ANIMATED_FALLBACK_FILENAME = "cursor.ani"

const csrfToken = () => {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

// Rails percent-escapes the ASCII `filename`; only `filename*` carries the name as sent.
const filenameFrom = (disposition: string | null, fallback: string) => {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  if (encoded) return decodeURIComponent(encoded)

  return disposition?.match(/filename="([^"]+)"/i)?.[1] || fallback
}

export class WindowsCursorsApi {
  static async create({
    frames,
    hotspotX,
    hotspotY,
    delayMs,
    alternate,
    resampling,
    filename,
  }: CreateWindowsCursorParams): Promise<WindowsCursorFile> {
    const isAnimated = frames.length > 1
    const body = new FormData()
    // File parts, not text fields: the server only accepts frames it can read as uploads.
    for (const pixels of frames) body.append("frames[]", new Blob([pixels]), "frame")
    body.set("hotspot_x", String(hotspotX))
    body.set("hotspot_y", String(hotspotY))
    if (isAnimated) {
      body.set("delay_ms", String(delayMs))
      body.set("alternate", String(alternate))
    }
    body.set("resampling", resampling)
    body.set("filename", filename)

    const response = await fetch(PATH, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      body,
    })
    if (response.status === 429) throw new RateLimitError()
    if (response.status === 422) {
      const { error } = (await response.json().catch(() => ({}))) as { error?: string }
      throw new ConversionError(error)
    }
    if (!response.ok) throw new Error(`Request failed (${response.status})`)

    return {
      blob: await response.blob(),
      filename: filenameFrom(
        response.headers.get("Content-Disposition"),
        isAnimated ? ANIMATED_FALLBACK_FILENAME : FALLBACK_FILENAME,
      ),
    }
  }
}
